import { RemoveCircleOutlineRounded } from "@mui/icons-material";
import NorthRoundedIcon from "@mui/icons-material/NorthRounded";
import { Box, Chip, Grid, Tab, Tabs, Typography } from "@mui/material";
import pluralize from "pluralize";
import { useContext, useState } from "react";
import useColors from "../../../../api/hooks/useColors";
import CustomToolTip from "../../../../components/CustomTooltip";
import StyledDataGrid from "../../../../components/StyledDataGrid";
import { CustomerFeatureFlagsContext } from "../../../../contexts/CustomerFeatureFlagsContext";
import SvgToIcon from "../../../../icons/SvgToIcon";
import { ControlStatus } from "../../../../utils/constants";
import { getPrettyDateTime, useLocalTime } from "../../../../utils/timestamp";
import ChipColor from "../../../remediation/plan/components/ChipColor";
import { icons } from "../../../stack/environment/icons/iconExport";
import { IntegrationsMapping } from "../../../stack/integrations/IntegrationsMapping";
import InvolvedEntityChip from "../../controls/components/EntitiesSection/InvolvedEntityChip";
import {
  getEntitiesLabel,
  getStatusColor,
  groupEntityIdsByType,
} from "../../controls/components/EntitiesSection/utils";
import NISTLabel from "../../controls/components/NISTLabel";
import { ReactComponent as NISTLogo } from "../../controls/components/NISTLogo.svg";
import DetectionLabelIcon from "../../detections/components/DetectionLabelIcon";
import DetectionsTooltip from "../../detections/components/DetectionsTooltip";

export default function TabbedMitigationControlsTable({
  mitigatingControls,
  handleCheckedControls = () => {},
  loading,
  onRowClick,
  checkbox = false,
  boxSXProps = {},
  pageSizeOptionsOverride = [5, 10, 20],
  filterColumns = [],
  affectedEntityIdsFilter = [], // This filter allows to show only relevant entities (e.g. for a specific step)
  isGapsTabInitialValue = true,
}) {
  const { detectionsActive } = useContext(CustomerFeatureFlagsContext);
  const colors = useColors();
  const showLocalTime = useLocalTime();

  const tabStyle = {
    "&.Mui-selected": {
      boxShadow: colors.rounded_tabs_shadow,
      border: "1px solid " + colors.grey_8percent,
      borderRadius: "8px 8px 0 0", // Rounded top corners
    },
    textTransform: "none",
    minHeight: "48px",
    alignContent: "center",
  };

  const rows = mitigatingControls?.map((row, index) => ({
    id: row.control_id,
    ...row,
  }));

  const gapRows = rows.filter(
    (row) => row.is_gap && !row.is_excluded && !row.is_snoozed
  );
  const compliantRows = rows.filter(
    (row) => !row.is_gap && !row.is_excluded && !row.is_snoozed
  );
  const snoozedRows = rows.filter((row) => row.is_snoozed);
  const excludedRows = rows.filter((row) => row.is_excluded);

  const [selectedTab, setSelectedTab] = useState(
    gapRows.length !== 0 && isGapsTabInitialValue
      ? ControlStatus.GAP
      : ControlStatus.COMPLIANT
  );

  const columns = [
    {
      field: "control_id",
      headerName: "ID",
      headerAlign: "center",
      align: "center",
      flex: 0.1,
      renderCell: (params) => (
        <CustomToolTip title={params.value} placement={"left"}>
          <Typography variant="body2">#{params.value}</Typography>
        </CustomToolTip>
      ),
    },
    ...(selectedTab === ControlStatus.GAP ||
    selectedTab === ControlStatus.EXCLUDED
      ? [
          {
            field: "name_for_gap_state",
            headerName: "Name",
            flex: 0.4,
            renderCell: (params) => {
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {params.row.is_snoozed && (
                    <CustomToolTip
                      title={
                        params.row.snooze_details &&
                        params.row.snooze_details?.snoozed_at && (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              gap: 0.5,
                              alignItems: "center",
                            }}
                          >
                            <Typography variant="caption">
                              {"Snoozed on"}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: colors.text_primary,
                              }}
                            >
                              {getPrettyDateTime(
                                params.row.snooze_details?.snoozed_at,
                                showLocalTime
                              )}
                              {params.row.snooze_details?.snoozed_by_name &&
                                ` by ${params.row.snooze_details.snoozed_by_name}`}
                            </Typography>
                          </Box>
                        )
                      }
                      placement={"top"}
                    >
                      <Box
                        sx={{
                          backgroundColor: colors.secondary_12percent,
                          borderRadius: "8px",
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <SvgToIcon
                          IconLogo={icons.SnoozedIcon}
                          width="18px"
                          style={{
                            color: colors.snoozed_status,
                          }}
                        />
                      </Box>
                    </CustomToolTip>
                  )}
                  {params.row.is_excluded && (
                    <CustomToolTip
                      title={
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 0.5,
                            alignItems: "center",
                          }}
                        >
                          <Typography variant="caption">
                            {"This control has been excluded"}
                          </Typography>
                          {params.row.excluded_at?.timestamp_utc && (
                            <>
                              <Typography variant="caption">{"on"}</Typography>
                              <Typography
                                variant="caption"
                                sx={{
                                  color: colors.text_primary,
                                }}
                              >
                                {getPrettyDateTime(
                                  params.row.excluded_at?.timestamp_utc,
                                  showLocalTime
                                )}
                              </Typography>
                            </>
                          )}
                        </Box>
                      }
                      placement={"top"}
                    >
                      <Box
                        sx={{
                          backgroundColor: colors.grey_8percent,
                          borderRadius: "8px",
                          border: "1px solid " + colors.grey_16percent,
                          padding: "4px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <RemoveCircleOutlineRounded
                          sx={{
                            width: "16px",
                            height: "16px",
                            color: colors.grey_700,
                          }}
                        />
                      </Box>
                    </CustomToolTip>
                  )}
                  <CustomToolTip title={params.value} placement={"left"}>
                    <Typography
                      variant="body2"
                      noWrap
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {params.value}
                    </Typography>
                  </CustomToolTip>
                </Box>
              );
            },
          },
        ]
      : [
          {
            field: "name_for_correct_state",
            headerName: "Name",
            flex: 0.4,
            renderCell: (params) => {
              const maxSeverity = params.row?.last_detections.length
                ? Math.max(
                    ...params.row?.last_detections.map(
                      (detection) => detection.severity
                    )
                  )
                : 0;
              return (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  {detectionsActive && params.row?.is_detected && (
                    <Box>
                      <DetectionsTooltip
                        content={
                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color={colors.common_black}
                              >
                                {params.row?.last_detections.length +
                                  pluralize(
                                    " Active event",
                                    params.row?.last_detections.length
                                  ) +
                                  ". Click to view details"}
                              </Typography>
                              <Typography variant="caption">
                                Highest Severity:
                              </Typography>
                              <ChipColor
                                value={maxSeverity}
                                type={"controlPriority"}
                                size="small"
                                labelWidth={true}
                              />
                            </Box>
                            <Typography variant="caption">
                              Click the control to view details
                            </Typography>
                          </Box>
                        }
                      >
                        <DetectionLabelIcon />
                      </DetectionsTooltip>
                    </Box>
                  )}
                  <CustomToolTip title={params.value} placement={"left"}>
                    <Typography
                      variant="body2"
                      noWrap
                      style={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {params.value}
                    </Typography>
                  </CustomToolTip>{" "}
                </Box>
              );
            },
          },
        ]),
    {
      field: "tool",
      headerName: "Tool",
      headerAlign: "center",
      flex: 0.15,
      align: "center",
      renderCell: (params) => {
        return (
          <SvgToIcon
            IconLogo={IntegrationsMapping[params.value?.toLowerCase()]?.logo}
            width="40px"
          />
        );
      },
    },
    {
      field: "nist_function",
      renderHeader: () => <SvgToIcon IconLogo={NISTLogo} width="40px" />,
      flex: 0.1,
      headerAlign: "left",
      renderCell: (params) => {
        return (
          <CustomToolTip title={"NIST CSF: " + params.value} placement={"top"}>
            <Box>
              <NISTLabel nistFunction={params.value} />
            </Box>
          </CustomToolTip>
        );
      },
    },
    {
      field: "severity",
      headerName: "Severity",
      flex: 0.22,
      renderCell: (params) => (
        <ChipColor
          value={params.value}
          type={"controlPriority"}
          disabled={!params.row.is_gap}
        />
      ),
    },
    ...(selectedTab === ControlStatus.GAP ||
    selectedTab === ControlStatus.EXCLUDED
      ? [
          {
            field: "affected_entity_counts_by_type",
            headerName: "Affected Entities",
            width: 200,
            disableClickEventBubbling: true,
            renderCell: (params) => {
              const countsByType = params.value;
              if (!countsByType || Object.keys(countsByType).length === 0) {
                return null;
              }

              const affectedEntitiesFilterByType = groupEntityIdsByType(
                affectedEntityIdsFilter
              );
              const affectedEntitiesFilterTypes = Object.keys(
                affectedEntitiesFilterByType
              );

              const entries = Object.entries(countsByType);

              // Remove types that are not in the filter
              let filteredEntries = entries.filter(([type]) =>
                affectedEntitiesFilterTypes.includes(type)
              );

              // Limit counts to not exceed the actual affected entities count in the filter for each type
              filteredEntries = filteredEntries.map(([type, count]) => {
                const affectedCountForType =
                  affectedEntitiesFilterByType[type]?.length || 0;
                const limitedCount = Math.min(count, affectedCountForType);
                return [type, limitedCount];
              });

              const firstEntry = filteredEntries[0];
              const remainingEntries = filteredEntries.slice(1);
              const tooltipTitle = remainingEntries
                .map(([type, count]) => getEntitiesLabel(count, type))
                .join(", ");

              // TODO: This will be updated in https://linear.app/breeze-security/issue/DEV-2543/consolidate-entity-chip-components
              return (
                <Box
                  display={"flex"}
                  flexDirection={"row"}
                  gap={1}
                  sx={{ marginLeft: "10px" }}
                  // Make this box unclickable so it won't confuse the user. Because when clicking on the chip, it will open the remediation drawer.
                  onClick={(event) => event.stopPropagation()}
                >
                  {firstEntry && (
                    <Grid item key={firstEntry[0]}>
                      <InvolvedEntityChip
                        type={firstEntry[0]}
                        entitiesCount={firstEntry[1]}
                        setSelectedEntities={null}
                        control={params.row}
                        sx={{ height: "24px" }}
                      />
                    </Grid>
                  )}
                  {remainingEntries && remainingEntries.length > 0 && (
                    <CustomToolTip title={tooltipTitle}>
                      <Chip
                        label={`+${remainingEntries.length}`}
                        style={{
                          minWidth: "24px",
                          background: colors.entity_chip_enabled_fill,
                          boxShadow: colors.entity_chip_enabled_shadow,
                          borderRadius: "8px",
                          fontSize: "12px",
                          color: colors.text_primary,
                          cursor: "auto",
                          userSelect: "none",
                          height: "24px",
                        }}
                      />
                    </CustomToolTip>
                  )}
                </Box>
              );
            },
          },
          {
            field: "feasibility",
            headerName: "Feasibility",
            flex: 0.2,
            renderCell: (params) => {
              return <ChipColor value={params.value} type={"feasibility"} />;
            },
          },
          {
            field: "improvement",
            headerName: "Impact",
            flex: 0.2,
            align: "center",
            renderCell: (params) => {
              return (
                <>
                  {params.value > 0 ? (
                    <Box
                      display={"flex"}
                      flexDirection={"row"}
                      gap={1}
                      alignItems={"center"}
                      sx={{
                        color: colors.success_dark,
                      }}
                    >
                      <NorthRoundedIcon
                        sx={{
                          width: "16px",
                          height: "16px",
                        }}
                      />
                      <Typography variant="caption">
                        +{params.value}%
                      </Typography>
                    </Box>
                  ) : (
                    <Typography variant="caption">-</Typography>
                  )}
                </>
              );
            },
          },
        ]
      : []),
  ];

  const [sortModel, setSortModel] = useState([
    {
      field: "status",
      sort: "desc",
    },
    {
      field: "severity",
      sort: "desc",
    },
  ]);
  const displayedColumns =
    filterColumns.length > 0
      ? columns.filter((column) => filterColumns.includes(column.field))
      : columns;

  const gapTab = (
    <Tab
      label={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              backgroundColor: getStatusColor(ControlStatus.GAP, colors),
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              marginLeft: "10px",
              marginRight: "10px",
            }}
          />
          <Typography variant="subtitle2" sx={{ marginRight: "10px" }}>
            Gap ({gapRows.length})
          </Typography>
        </Box>
      }
      value={ControlStatus.GAP}
      disabled={gapRows.length === 0}
      sx={{
        ...tabStyle,
      }}
    />
  );

  const compliantTab = (
    <Tab
      label={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              backgroundColor: getStatusColor(ControlStatus.COMPLIANT, colors),
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              marginLeft: "10px",
              marginRight: "10px",
            }}
          />
          <Typography variant="subtitle2" sx={{ marginRight: "10px" }}>
            Compliant ({compliantRows.length})
          </Typography>
        </Box>
      }
      value={ControlStatus.COMPLIANT}
      disabled={compliantRows.length === 0}
      sx={{
        ...tabStyle,
      }}
    />
  );

  const snoozedTab = (
    <Tab
      label={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              backgroundColor: getStatusColor(ControlStatus.SNOOZED, colors),
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              marginLeft: "10px",
              marginRight: "10px",
            }}
          />
          <Typography variant="subtitle2" sx={{ marginRight: "10px" }}>
            Snoozed ({snoozedRows.length})
          </Typography>
        </Box>
      }
      value={ControlStatus.SNOOZED}
      disabled={snoozedRows.length === 0}
      sx={{
        ...tabStyle,
      }}
    />
  );

  const excludedTab = (
    <Tab
      label={
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            sx={{
              backgroundColor: getStatusColor(ControlStatus.EXCLUDED, colors),
              width: "10px",
              height: "10px",
              borderRadius: "50%",
              marginLeft: "10px",
              marginRight: "10px",
            }}
          />
          <Typography variant="subtitle2" sx={{ marginRight: "10px" }}>
            Excluded ({excludedRows.length})
          </Typography>
        </Box>
      }
      value={ControlStatus.EXCLUDED}
      disabled={excludedRows.length === 0}
      sx={{
        ...tabStyle,
      }}
    />
  );

  return (
    <Grid
      container
      display="flex"
      direction={"column"}
      justifyContent="space-between"
      width={"100%"}
      flexWrap={"nowrap"}
      sx={{
        boxShadow: " 0px 0px 2px 1px rgba(203, 203, 203, 0.50)",
        background: "white",
        borderRadius: "10px",
        overflow: "hidden",
      }}
    >
      <Grid item>
        <Tabs
          sx={{
            ".MuiTabs-indicator": {
              display: "none",
              boxShadow: "0px 0px 15px 2px " + colors.wizard_box_shadow,
            },
          }}
          value={selectedTab}
          onChange={(event, newValue) => {
            setSelectedTab(newValue);
          }}
        >
          {gapRows.length === 0 ? (
            <CustomToolTip title={"No gaps found"}>
              <Box>{gapTab}</Box>
            </CustomToolTip>
          ) : (
            gapTab
          )}
          {compliantRows.length === 0 ? (
            <CustomToolTip title={"No compliant controls found"}>
              <Box>{compliantTab}</Box>
            </CustomToolTip>
          ) : (
            compliantTab
          )}
          {snoozedRows.length === 0 ? (
            <CustomToolTip title={"No snoozed controls found"}>
              <Box>{snoozedTab}</Box>
            </CustomToolTip>
          ) : (
            snoozedTab
          )}
          {excludedRows.length === 0 ? (
            <CustomToolTip title={"No excluded controls found"}>
              <Box>{excludedTab}</Box>
            </CustomToolTip>
          ) : (
            excludedTab
          )}
        </Tabs>
      </Grid>

      <Grid item xs={12} style={{ flexGrow: 1, width: "100%" }}>
        {(selectedTab === ControlStatus.GAP
          ? gapRows
          : selectedTab === ControlStatus.COMPLIANT
          ? compliantRows
          : selectedTab === ControlStatus.SNOOZED
          ? snoozedRows
          : excludedRows
        )?.length > 0 ? (
          <StyledDataGrid
            checkboxSelection={checkbox}
            sx={{
              cursor: "pointer",
              overflowX: "auto",
              border: "0px",
              "& .row-disabled": {
                opacity: 0.6,
              },
            }}
            columns={displayedColumns}
            rows={
              selectedTab === ControlStatus.GAP
                ? gapRows
                : selectedTab === ControlStatus.COMPLIANT
                ? compliantRows
                : selectedTab === ControlStatus.SNOOZED
                ? snoozedRows
                : excludedRows
            }
            onCellClick={(params, event) => {
              if (params.field !== "__check__") {
                event.stopPropagation();
                const control_id = params.row.control_id;
                onRowClick(control_id);
              }
            }}
            loading={loading}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  platform: false,
                },
              },
            }}
            sortModel={sortModel}
            onSortModelChange={(model) => setSortModel(model)}
            onRowSelectionModelChange={handleCheckedControls}
            boxSXProps={boxSXProps}
            pageSizeOptionsOverride={pageSizeOptionsOverride}
            getRowClassName={(params) =>
              params.row.is_excluded ? "row-disabled" : ""
            }
          />
        ) : (
          <Grid sx={{ padding: "16px" }}>
            <Typography variant="subtitle2">
              No mitigating controls found
            </Typography>
          </Grid>
        )}
      </Grid>
    </Grid>
  );
}
